/** localStorage key shared by PrivateRoute, Landing, 401, and prepareHeaders. */
export const AUTH_REDIRECT_URL_KEY = "auth_redirect_url";

const PUBLIC_OR_ERROR_PREFIXES = [
  "/error/",
  "/invitation",
  "/unverified-email",
  "/verified-email",
  "/verify-login",
];

const PUBLIC_OR_ERROR_EXACT = new Set([
  "/",
  "/401",
  "/404",
  "/register",
  "/invitation",
  "/application",
  "/application/register",
  "/application/affordable/prequalify",
]);

/**
 * Paths we should not stash as post-login returnTo (public screens, error
 * pages, or anything that would create a redirect loop).
 */
export function shouldSkipAuthReturnTo(pathWithSearch: string): boolean {
  const pathname =
    (pathWithSearch.split("?")[0] || "/").replace(/\/$/, "") || "/";
  if (PUBLIC_OR_ERROR_EXACT.has(pathname)) return true;
  if (pathname.startsWith("/property/")) return true;
  if (pathname.startsWith("/availability/")) return true;
  return PUBLIC_OR_ERROR_PREFIXES.some(
    (p) => pathname === p.replace(/\/$/, "") || pathname.startsWith(p)
  );
}

/** Persist path+query so Auth0 loginWithRedirect can return the applicant here. */
export function stashAuthReturnTo(returnTo?: string): void {
  if (typeof window === "undefined") return;
  const value =
    returnTo ?? `${window.location.pathname}${window.location.search}`;
  if (!value.startsWith("/") || shouldSkipAuthReturnTo(value)) return;
  try {
    localStorage.setItem(AUTH_REDIRECT_URL_KEY, value);
  } catch {
    // private mode / quota — soft-recover still works via appState when set inline
  }
}

export function readAuthReturnTo(fallback = "/"): string {
  try {
    const stored = localStorage.getItem(AUTH_REDIRECT_URL_KEY);
    if (stored && stored.startsWith("/") && !shouldSkipAuthReturnTo(stored)) {
      return stored;
    }
  } catch {
    // ignore
  }
  return fallback.startsWith("/") ? fallback : "/";
}

export function clearAuthReturnTo(): void {
  try {
    localStorage.removeItem(AUTH_REDIRECT_URL_KEY);
  } catch {
    // ignore
  }
}

/**
 * True when returnTo is the application-fee payment page (pay-first or legacy).
 * Soft-recover / PrivateRoute often stash this mid-pay; after the fee is settled
 * we must NOT send the resident back here on the next login.
 */
export function parsePaymentApplicationId(
  pathWithSearch: string
): string | null {
  if (!pathWithSearch || !pathWithSearch.startsWith("/")) return null;
  try {
    const url = new URL(pathWithSearch, "https://local.invalid");
    const path = url.pathname.replace(/\/$/, "") || "/";
    if (path !== "/application/payment") return null;
    const id = url.searchParams.get("applicationId");
    return id && id.trim() ? id.trim() : null;
  } catch {
    return null;
  }
}

/** Path used while we resolve whether a payment returnTo is still valid. */
export function authResumePath(returnTo: string): string {
  return `/auth/resume?to=${encodeURIComponent(returnTo)}`;
}
